import { DEFAULT_STATUS_TYPE } from "data/error-mapping";
import { IBusiness, IObject } from "./base";
import { Team } from "./team-management";

export interface IMeta {
  page: number;
  limit: number;
  total: number;
  nextPage: number;
  pageCount: number;
  previousPage: number;
}

export type TTransactionLoading =
  | "getTransactions"
  | "getSingleTransaction"
  | "getTransactionById"
  | "getRecentTransactions"
  | "getConversionTransactions"
  | "getAllWithdrawals"
  | "getAirtimeDataTransactions"
  | "getCardTransactions"
  | "getTransactionStatement";

export type PaginationState = {
  currentPage: number;
  hasNext?: boolean;
  hasPrevious?: boolean;
  lastPage?: number;
  next?: number;
  pageSize: number;
  prevPage?: number;
  total?: number;
};

export type NewPaginationState = {
  limit?: number;
  nextPage?: number;
  page?: number;
  pageCount?: number;
  previousPage?: number;
  total?: number;
};

export interface ITransaction {
  amount: number;
  caller: string;
  callerId: any;
  createdAt: string;
  currency: string;
  description: string;
  metadata: any;
  nextTotal: number;
  previousTotal: number;
  type: string;
  updatedAt: string;
  userId: string;
  __v: number;
  _id: string;
}

export interface ITransactionError {
  msg: string;
  for: TTransactionLoading;
}

export interface ITransactionsState {
  data: ITransaction[];
  pagination: NewPaginationState;
}

export interface ITransactionState {
  getAllTransactions: {
    error: string;
    pagination: IMeta;
    isLoading: boolean;
    data: ITransaction[];
  };
  getTransactionCallers: { error: string; data: string[]; isLoading: boolean };
  error: null | string;
  airtimeData: ITransactionsState;
  conversions: ITransactionsState;
  transactions: ITransactionsState;
  withdrawal: ITransactionsState;
  withdrawals: ITransactionsState;
  sendMoney: ITransactionsState;
  filter: Filter;
  statement: {
    card: any[];
    ticket: any[];
    funding: any[];
    withdrawal: any[];
    paySomeone: any[];
    conversion: any[];
    airtimeData: any[];
  };
  receentTransactions: ITransactionsState;
  cardTransactions: ICardTransactionsState;
  transaction: ITransaction | null;
  transactionById: ITransaction | null;
  selectedTransactionId: number | null;
  transactionSummary: {
    error: string;
    data: ITransactionSummary | null;
    loading: boolean;
  };

  status: {
    getTransactions: DEFAULT_STATUS_TYPE;
    getTransactionById: DEFAULT_STATUS_TYPE;
    getSingleTransaction: DEFAULT_STATUS_TYPE;
    getRecentTransactions: DEFAULT_STATUS_TYPE;
    getConversionTransactions: DEFAULT_STATUS_TYPE;
    getAirtimeDataTransactions: DEFAULT_STATUS_TYPE;
    getAllWithdrawals: DEFAULT_STATUS_TYPE;
    getCardTransactions: DEFAULT_STATUS_TYPE;
    getTransactionStatement: DEFAULT_STATUS_TYPE;
  };
  changingStatus: boolean;
}

export enum ChangeraTransactionType {
  AIRTIME_VTU = "airtime-vtu",
  DATA_VTU = "data-vtu",
  PAY_MONEY = "pay-money",
  SEND_MONEY = "send-money",
  FAUCET = "faucet",
  REFERRAL = "referral",
  REWARD = "reward",
  FUNDING_WALLET = "fund-wallet",
  FUNDING_SUB_WALLET = "fund-sub-wallet",
  WITHDRAWAL = "withdrawal",
  FIAT_CONVERSION = "fiat-conversion",
  CRYPTO_CONVERSION = "crypto-conversion",
  VIRTUAL_CARD = "virtual-card",
  FX = "fx"
}

export interface ITransactionSummary {
  total: [
    {
      type: string;
      total: string;
    },
    {
      type: string;
      total: string;
    }
  ];
  yearlyData: IObject[];
  monthlyData: IObject[];
  weeklyData: IObject[];
  dailyData: IObject[];
}

export interface IWithdrawalTransaction {
  id: number;
  currency: string;
  currencyType: string;
  providusSettlementId: any;
  amount: string;
  signedAmount: string;
  rate: string;
  type: string;
  subType: string;
  fxInvoice?: string;
  fxExternalAccount?: string;
  changeraTransactionType: string;
  paymentGateways: any;
  serviceTransactionId: any;
  reference: string;
  generalTransactionReference: string;
  sourceAccountNumber: any;
  sourceAccountName: any;
  providusTranRemarks: any;
  sourceBankName: any;
  description: string;
  status: string;
  balanceAfter: string;
  balanceBefore: string;
  hash: any;
  metaData: any;
  address: any;
  createdAt: string;
  updatedAt: string;
  plan: any;
  network: any;
  phone: any;
  lien: any;
  cardName: any;
}

export interface ICardTransaction {
  id: number;
  currency: string;
  currencyType: string;
  providusSettlementId: any;
  amount: string;
  signedAmount: string;
  rate: string;
  type: string;
  subType: string;
  changeraTransactionType: string;
  paymentGateways: any;
  serviceTransactionId?: string;
  reference: string;
  generalTransactionReference: string;
  sourceAccountNumber: any;
  sourceAccountName: any;
  providusTranRemarks: any;
  sourceBankName: any;
  description: string;
  status: string;
  balanceAfter: string;
  balanceBefore: string;
  hash: any;
  metaData: any;
  address: any;
  createdAt: string;
  updatedAt: string;
  plan: any;
  network: any;
  phone: any;
  lien: any;
  cardName?: string;
  reason: any;
  member: Team;
  business: Partial<IBusiness>;
}

export interface ICardTransactionsState {
  data: ICardTransaction[];
  pagination: PaginationState;
}
type TransactionType = "credit" | "debit" | "both";
type TransactionDateType = "day" | "month" | "year" | "custom";
type TransactionStatus = "success" | "pending" | "failed" | "all";

export type Filter = {
  currency?: string;
  date?: string | Date;
  from?: string | Date;
  fiatWallet?: number;
  dateFormat?: string;
  cryptoWallet?: number;
  type?: TransactionType;
  showYearPicker?: boolean;
  status?: TransactionStatus;
  showMonthYearPicker?: boolean;
  dateType?: TransactionDateType;
  dataFormat?: string;
};

export type GenerateTransactionStatementBody = {
  type: ChangeraTransactionType;
  dataFormat: string;
};
